import { useEffect } from "react";

/**
 * Reveals elements with the `.reveal` class when they enter the viewport.
 * Uses a MutationObserver so lazy-loaded sections (added to the DOM later
 * via React.lazy / Suspense) are picked up automatically — without this,
 * deferred sections stay stuck at opacity: 0 forever.
 *
 * Mutation callbacks are batched via requestAnimationFrame to avoid forced
 * reflows from running querySelectorAll synchronously on every DOM change.
 *
 * Pass any value as `dep` to force a re-scan when content changes.
 */
export function useReveal(dep?: unknown) {
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" },
    );

    const observe = (root: ParentNode = document) => {
      root
        .querySelectorAll<HTMLElement>(".reveal:not(.is-visible)")
        .forEach((el) => io.observe(el));
    };

    // Initial scan
    observe();

    // Batch mutation handling to avoid forced reflows.
    let scheduled = false;
    const pending: HTMLElement[] = [];
    const flush = () => {
      scheduled = false;
      // De-duplicate by processing each node once.
      const seen = new Set<HTMLElement>();
      for (const node of pending) {
        if (seen.has(node) || !node.isConnected) continue;
        seen.add(node);
        if (node.classList?.contains("reveal") && !node.classList.contains("is-visible")) {
          io.observe(node);
        }
        observe(node);
      }
      pending.length = 0;
    };

    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) pending.push(node);
        });
      }
      if (pending.length && !scheduled) {
        scheduled = true;
        requestAnimationFrame(flush);
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      io.disconnect();
      mo.disconnect();
    };
  }, [dep]);
}
